'use client';

import { parseProductVariantIntoLink } from '@/ui/components/shared/ButtonLink/utils/parse-product-variant-into-link';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React from 'react';
import { useCartStore } from '@/ui/store/cart';
import { buildTrackingId } from '@/utils/tracking-helper';
import { isProductVariantInStock } from '@/ui/services/utils/isVariantInStock';
import { ProductTableProductVariantFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { Bundle } from '@/ui/services/BundleService';
import * as ProductVariantHelper from '@/integrations/crystallize/helpers/product-variant-helper';

interface VariantCardProps {
  variant?: ProductTableProductVariantFragment;
  bundle?: Bundle;
  trackingPrefix?: string;
}

export default function VariantCard({
  variant,
  bundle,
  trackingPrefix,
}: Readonly<VariantCardProps>) {
  const { formatCurrency } = useCurrencyFormatter();
  const { localizedLink } = useLocaleLink();
  const currentPath = usePathname();
  const cart = useCartStore();
  const t = useTranslations('ProductCards');

  let productImage;
  let productName;
  let productShortDescription;
  let productPrice;
  let productPriceBeforeDiscount;
  let productPath;
  let addToCart = false;

  // Defaults to true so bundles and in-stock variants show the "+"; only a direct-add variant that
  // is out of stock flips this to false (→ "Notify me" button).
  let isInStock = true;
  let addToCartFunction = () => {};

  if (variant) {
    productImage = variant.images?.[0];
    productName = variant.name;
    // Prefer the variant-level "Variant description" (spare parts); fall back to the product
    // short description so accessory cards keep their copy.
    productShortDescription =
      ProductVariantHelper.getVariantDescriptionBlocks(variant) ??
      componentByTypename(variant.product?.shortDescription?.content, 'RichTextContent');
    productPrice = variant.priceVariant;

    if (ProductVariantHelper.isDirectAddToCartVariant(variant)) {
      // "Show chooser" off → add this SKU straight to the cart in-page. Out-of-stock variants
      // switch the button to a "Notify me" state that opens the back-in-stock dialog.
      const parsedVariant = ProductVariantHelper.parseVariantForStock(variant);
      addToCart = true;
      isInStock = isProductVariantInStock(parsedVariant);

      addToCartFunction = () => {
        if (!parsedVariant.priceVariant) {
          return;
        }

        if (isInStock) {
          cart.addCartItem(ProductVariantHelper.buildCartItemFromVariant(variant));
          // Open the cart, but don't toggle it closed when it is already open and clicking "+" on product card level
          if (!useCartStore.getState().isCartDisplayed) {
            cart.toggleCartDisplay();
          }
        } else {
          cart.setShowNotifyMe(true, parsedVariant, null);
        }
      };
    } else {
      productPath = parseProductVariantIntoLink(variant, currentPath, true);
    }
  }

  if (bundle) {
    productImage = bundle.images?.[0];
    productName = bundle.name;
    const linkPath = bundle.getLinkPath();
    // The chooser opens as an overlay over the current page, a bundle page scrolls to top
    productPath = { href: linkPath, scroll: linkPath !== bundle.getChooserPath() };
    // In case bundle doesnt have product that has a chooser/pdp add to cart
    if (!productPath.href) {
      addToCart = true;
      addToCartFunction = () => {
        bundle.checkStockAndAddToCart(bundle.defaultBundleProductVariants, cart);
      };
    }
    productPrice = bundle.finalPrice;
    productPriceBeforeDiscount = bundle.totalOriginalPrice;
  }

  const buyTrackingId = buildTrackingId(trackingPrefix, productName, 'buy');
  const addToCartTrackingId = buildTrackingId(trackingPrefix, productName, 'add-to-cart');

  return (
    <div className="w-full">
      <div className=" relative z-0 h-37 overflow-hidden rounded-3xl border border-neutral-10 py-4 sm:h-50">
        <div className=" absolute bottom-0 left-0 right-0 -z-10 h-1/4 bg-gradient-to-t from-neutral-5 to-transparent opacity-40" />
        <OptimizedImage
          image={productImage}
          width={300}
          height={300}
          className=" z-10 h-full w-full object-contain"
          sizes="(max-width: 640px) 90vw, (max-width: 1024px) 40vw, 20vw"
        />
        {productPath?.href && (
          <Link
            href={localizedLink(productPath?.href)}
            scroll={productPath?.scroll}
            aria-label="Buy"
            className=" absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full border-4 border-primary-100 bg-primary-100 text-3xl font-bold duration-200 hover:bg-white"
            data-tile-element-id={buyTrackingId}
          >
            +
          </Link>
        )}
        {addToCart && !isInStock && (
          <button
            type="button"
            onClick={addToCartFunction}
            aria-label={t('buttonOutOfStockNotifyMe')}
            className=" absolute bottom-2 right-2 flex h-10 items-center justify-center rounded-full border-4 border-primary-100 bg-primary-100 px-4 text-sm font-bold duration-200 hover:bg-white"
            data-tile-element-id={addToCartTrackingId}
          >
            {t('buttonOutOfStockNotifyMe')}
          </button>
        )}

        {addToCart && isInStock && (
          <button
            type="button"
            onClick={addToCartFunction}
            aria-label={t('addToCart')}
            className=" absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full border-4 border-primary-100 bg-primary-100 text-3xl font-bold duration-200 hover:bg-white"
            data-tile-element-id={addToCartTrackingId}
          >
            +
          </button>
        )}
      </div>
      <div className="my-1 space-y-1 sm:my-3">
        <h3 className=" font-bold sm:text-md">{productName}</h3>
        <RichTextRenderer
          blocks={productShortDescription}
          className="text-sm text-neutral-60 sm:w-4/5"
        />
        <div>
          {productPriceBeforeDiscount && productPriceBeforeDiscount !== productPrice && (
            <span className=" font-bold text-neutral-50 line-through">
              {formatCurrency(productPriceBeforeDiscount, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              })}
            </span>
          )}
          {productPrice && (
            <span className=" block font-bold">
              {formatCurrency(productPrice, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
